import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Download } from 'lucide-react';

export default function InstallPWAPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Check if user has dismissed the prompt recently (within 7 days)
    const dismissedAt = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissedAt) {
      const dismissedDate = new Date(parseInt(dismissedAt));
      const now = new Date();
      const daysDiff = (now - dismissedDate) / (1000 * 60 * 60 * 24);
      if (daysDiff < 7) {
        return; // Don't show prompt if dismissed less than 7 days ago
      }
    }

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return; // Already installed as PWA
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS Safari (which doesn't support beforeinstallprompt)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isIOS && isSafari) {
      // Show custom iOS instructions after a delay
      const timer = setTimeout(() => {
        if (!localStorage.getItem('pwa-prompt-dismissed')) {
          setShowPrompt(true);
        }
      }, 2000);
      return () => clearTimeout(timer);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    } else {
      // iOS - show instructions
      alert('Per installare su iOS:\n1. Tocca il pulsante Condividi\n2. Seleziona "Aggiungi a Home"');
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in">
      <div className="bg-[#111827] border border-gray-700 rounded-2xl p-6 mx-4 max-w-sm w-full shadow-2xl">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          <X size={20} />
        </button>

        {/* Logo */}
        <div className="flex justify-center mb-4">
          <img
            src="/icons/icon-any-192-v3.png"
            alt="Hedging Tracker"
            className="w-20 h-20 rounded-2xl shadow-lg object-contain"
          />
        </div>

        {/* Title */}
        <h2 className="text-xl font-bold text-white text-center mb-2">
          Hedging Tracker
        </h2>

        {/* Description */}
        <p className="text-gray-400 text-center text-sm mb-6">
          Installa l'app per accedervi facilmente dal tuo dispositivo senza aprire il browser
        </p>

        {/* Buttons */}
        <div className="flex flex-col gap-3">
          <Button
            onClick={handleInstall}
            className="w-full accent-bg accent-bg-hover text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2"
          >
            <Download size={18} />
            Installa
          </Button>
          <Button
            onClick={handleDismiss}
            variant="ghost"
            className="w-full text-gray-400 hover:text-white hover:bg-gray-800 py-3 rounded-xl"
          >
            Non ora
          </Button>
        </div>
      </div>
    </div>
  );
}
